import type { Message } from "@redotech/redo-agents-common/llm-messages";
import { T } from "@redotech/redo-i18n";
import { AiSlideOutChatGlobal } from "@redotech/redo-web/ai/chat/components/slide-out-chat-modal-global";
import {
  AiChatContext,
  AiChatProvider,
} from "@redotech/redo-web/ai/chat/contexts/ai-chat-context";
import { useAiSlidePanel } from "@redotech/redo-web/ai/chat/contexts/ai-slide-panel-context";
import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useProgress } from "../../contexts/progress-context";
import { OnboardingV2RpcClientContext } from "../../rpc-client";
import { FeedbackModal } from "../ui/feedback-modal";

/**
 * Reads `panelOptions.prefillInput` from the slide-panel context and pushes
 * it into the chat input value. Must be rendered inside both AiChatProvider
 * and AiSlidePanelProvider.
 */
const PrefillBridge = memo(function PrefillBridge() {
  const { panelOptions, clearPanelOptions } = useAiSlidePanel();
  const { setInputValue } = useContext(AiChatContext);

  useEffect(() => {
    if (panelOptions?.prefillInput) {
      setInputValue(panelOptions.prefillInput);
      clearPanelOptions();
    }
  }, [panelOptions, setInputValue, clearPanelOptions]);

  return null;
});

export const GlobalAskRedo = memo(function GlobalAskRedo({
  product,
  stepId,
  stepLabel,
  initialThreadIdRef,
}: {
  product: string;
  stepId: string;
  stepLabel: string;
  /** Ref holding the persisted active thread id to resume on initial load. */
  initialThreadIdRef?: React.MutableRefObject<string | undefined>;
}) {
  const rpcClient = useContext(OnboardingV2RpcClientContext);
  const { formData } = useProgress();
  const currentThreadIdRef = useRef<string | undefined>(
    initialThreadIdRef?.current,
  );

  // Keep currentThreadIdRef seeded if getProgress resolves after mount.
  useEffect(() => {
    if (
      initialThreadIdRef?.current !== undefined &&
      currentThreadIdRef.current === undefined
    ) {
      currentThreadIdRef.current = initialThreadIdRef.current;
    }
  }, [initialThreadIdRef]);

  const sendMessageToAI = useCallback(
    async ({
      newMessages,
    }: {
      messages: Message[];
      newMessages: Message[];
    }) => {
      if (!rpcClient) {
        return { messages: [] };
      }

      const result = await rpcClient.askRedo({
        messages: newMessages,
        currentThreadId: currentThreadIdRef.current,
        product,
        pageContext: { stepId, stepLabel, formValues: formData },
      });

      currentThreadIdRef.current = result.threadId;
      return { messages: result.messages };
    },
    [rpcClient, product, stepId, stepLabel, formData],
  );

  return (
    <AiChatProvider sendMessageToAI={sendMessageToAI}>
      <PrefillBridge />
      <AskRedoChat threadIdRef={currentThreadIdRef} />
    </AiChatProvider>
  );
});

const scheduleCallPromptStyle: React.CSSProperties = {
  fontSize: "var(--redo-font-size-xs)",
  lineHeight: "var(--redo-line-height-xs)",
  color: "var(--redo-colors-text-text-secondary, #667085)",
  textAlign: "center",
  padding: "4px 8px 6px",
};

const scheduleCallLinkStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  fontSize: "inherit",
  color: "inherit",
  textDecoration: "underline",
  textUnderlineOffset: "2px",
};

const AskRedoChat = memo(function AskRedoChat({
  threadIdRef,
}: {
  threadIdRef: React.MutableRefObject<string | undefined>;
}) {
  const { close } = useAiSlidePanel();
  const { clearMessages } = useContext(AiChatContext);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

  const handleNewConversation = useCallback(() => {
    threadIdRef.current = undefined;
    clearMessages();
  }, [threadIdRef, clearMessages]);

  const handleHelpModalClose = useCallback(() => {
    setIsHelpModalOpen(false);
  }, []);

  const scheduleCallPrompt = useMemo(
    () => (
      <div style={scheduleCallPromptStyle}>
        <T>
          Can't find the answer you're looking for?{" "}
          <button
            onClick={() => setIsHelpModalOpen(true)}
            style={scheduleCallLinkStyle}
            type="button"
          >
            Schedule a call.
          </button>
        </T>
      </div>
    ),
    [],
  );

  return (
    <>
      <AiSlideOutChatGlobal
        aboveInput={scheduleCallPrompt}
        hideClearButton
        onClose={close}
        onNewConversation={handleNewConversation}
      />
      <FeedbackModal
        initialReasonId="need-help"
        isOpen={isHelpModalOpen}
        onClose={handleHelpModalClose}
        onConfirm={handleHelpModalClose}
      />
    </>
  );
});
